// Catalogue contracts (docs/use-cases/catalogue.md, UC-CATALOGUE-001..011; modules-and-api.md §5.10; working decisions W1,
// W4, W6, W12, W13 and W15 of docs/sprints/sprint-04.md).
//
// Sprint 4 foundation: the vocabularies the catalogue's tables (their CHECK constraints) and routes share, spelled as the use
// cases spell them, their limits, and the pure helpers its unit cases prove here (TC-CATALOGUE-064, TC-CATALOGUE-089). The
// formats other modules share — SKUs, NAPPI and tariff codes, barcode check digits, serial numbers — are in primitives.ts and
// the generated barcode's format in sequences.ts.
//
// The catalogue module's build adds its request and response schemas (the end of this file). A body field answers the codes
// the use cases name — `required`, `too_long`, `invalid`, `out_of_range`, `not_whole` — through the small field builders
// below rather than Zod's own codes (`invalid_type`, `too_big`), and a measurement travels in its own unit with the decimals
// its rule allows (millimetres to one decimal, an index to three, dioptres to two), stored as a whole number on the schema's
// scale (tenths, thousandths, hundredths): a value with more decimals is `invalid`, a fraction where a whole number is due
// `not_whole`.
import { z } from "zod";
import { UNIT_COST_MAX_CENTS } from "./primitives";
import { SERIAL_STATUSES } from "./status-machines";
import { ResolvedSettingSchema } from "./tenancy";

/** BR-CATALOGUE-001: every product has exactly one category. */
export const PRODUCT_CATEGORIES = ["Frame", "PlasticLens", "GlassLens", "ContactLens", "Accessory", "Service"] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];
export const PRODUCT_CATEGORY_LABELS: Readonly<Record<ProductCategory, string>> = {
  Frame: "Frame", PlasticLens: "Plastic lens", GlassLens: "Glass lens", ContactLens: "Contact lens", Accessory: "Accessory", Service: "Service",
};
/** BR-CATALOGUE-005, BR-CATALOGUE-006: the two lens categories share their details, and a product may move between them. */
export const LENS_CATEGORIES = ["PlasticLens", "GlassLens"] as const satisfies readonly ProductCategory[];

/** BR-CATALOGUE-019: a product is Active or Inactive (a lens an import creates stays Inactive until its final write). */
export const PRODUCT_STATUSES = ["Active", "Inactive"] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

/** BR-CATALOGUE-005: VAT treatment, `Standard` by default (Standard: the organisation's rate; the others 0, BR-PRICING-010). */
export const VAT_TREATMENTS = ["Standard", "ZeroRated", "Exempt"] as const;
export type VatTreatment = (typeof VAT_TREATMENTS)[number];

/** BR-CATALOGUE-007: `Normal` (full-rim), `Nylon` (semi-rimless, grooved) or `Rimless` (drilled). */
export const FRAME_TYPES = ["Normal", "Nylon", "Rimless"] as const;
export type FrameType = (typeof FRAME_TYPES)[number];
/** BR-CATALOGUE-007: a frame's gender and age group. */
export const FRAME_GENDER_AGE_GROUPS = ["Men", "Women", "Unisex", "Kids"] as const;
export type FrameGenderAgeGroup = (typeof FRAME_GENDER_AGE_GROUPS)[number];

/**
 * BR-CATALOGUE-008: a lens's design type. `Occupational` (Seiko Indoor 1.60, FIXTURE §11.10) is read as Progressive wherever a
 * rule keys on CAT-20's four, until Sprint 5 or 7 says otherwise (UC-CATALOGUE-001, open questions). The supplier's lens type,
 * design code and material code are free text (`CATALOGUE_LIMITS`).
 */
export const LENS_DESIGN_TYPES = ["SingleVision", "Bifocal", "Trifocal", "Progressive", "Occupational"] as const;
export type LensDesignType = (typeof LENS_DESIGN_TYPES)[number];
/** BR-CATALOGUE-008: a lens's colour — `White` is shown Clear, as the lab order writes it. */
export const LENS_COLOURS = ["White", "PGX"] as const;
export type LensColour = (typeof LENS_COLOURS)[number];
export const LENS_COLOUR_LABELS: Readonly<Record<LensColour, string>> = { White: "Clear", PGX: "PGX" };
/** BR-CATALOGUE-008: the SOW's diameters, offered first; any whole 40–90 mm is allowed. */
export const LENS_DIAMETERS_OFFERED_MM = [65, 70, 75, 80] as const;

/**
 * BR-CATALOGUE-013: a product barcode's kind. Supplier EANs and generated barcodes print as EAN-13, UPCs as UPC-A, alternates
 * as Code 128 (labels: Sprint 13). A serialised product holds none: each unit's barcode is its serial (BR-CATALOGUE-014).
 */
export const BARCODE_KINDS = ["SupplierEAN", "SupplierUPC", "Generated", "Alternate"] as const;
export type BarcodeKind = (typeof BARCODE_KINDS)[number];

/** BR-CATALOGUE-017 (W13): a lens lab's component option — a coating, a colour (shown Tint) or a photochromic or polarising filter. */
export const LENS_COMPONENT_OPTION_KINDS = ["Coating", "Colour", "PPF"] as const;
export type LensComponentOptionKind = (typeof LENS_COMPONENT_OPTION_KINDS)[number];
export const LENS_COMPONENT_OPTION_KIND_LABELS: Readonly<Record<LensComponentOptionKind, string>> = { Coating: "Coating", Colour: "Tint", PPF: "PPF" };

/** BR-CATALOGUE-018: a service-combination rule's type, stored now and checked on orders from Sprint 5 (R-20). */
export const SERVICE_COMBINATION_RULE_TYPES = ["MutuallyExclusive", "Requires", "MaxQuantityPerOrder", "OncePerPatientPerNDays"] as const;
export type ServiceCombinationRuleType = (typeof SERVICE_COMBINATION_RULE_TYPES)[number];
/** BR-CATALOGUE-018: `Block` (the order cannot be saved) or `Warn` (it is saved with a reason). */
export const SERVICE_COMBINATION_SEVERITIES = ["Block", "Warn"] as const;
export type ServiceCombinationSeverity = (typeof SERVICE_COMBINATION_SEVERITIES)[number];
/** BR-CATALOGUE-018: a `Requires` rule's members are Trigger and Required; every other type's are plain members. */
export const SERVICE_COMBINATION_MEMBER_ROLES = ["Member", "Trigger", "Required"] as const;
export type ServiceCombinationMemberRole = (typeof SERVICE_COMBINATION_MEMBER_ROLES)[number];

/**
 * The limits the catalogue's use cases state, for the routes' schemas and the tables' CHECKs alike. Measurements in tenths
 * of a millimetre, the index in thousandths, powers in hundredths of a dioptre — whole numbers, as every stored quantity is.
 */
export const CATALOGUE_LIMITS = {
  /** BR-CATALOGUE-005 */
  nameMax: 120, descriptionMax: 1_000, brandMax: 60, tagMax: 20, subCategoryMax: 40,
  /** BR-CATALOGUE-011: the supplier's own code for the product. */
  supplierCodeMax: 40,
  /** BR-CATALOGUE-026: a supplier price is at most R 999 999.99 ex VAT, the cap of every cost a person types (UNIT_COST_MAX_CENTS). */
  supplierPriceCentsMax: UNIT_COST_MAX_CENTS,
  /** BR-CATALOGUE-007: frame details — text lengths, and A, DBL, B, ED and temple more than 0 and at most 200.0 mm. */
  hoyalogFrameTypeCodeMax: 10, modelNameMax: 60, modelNumberMax: 30, colourCodeMax: 20, colourNameMax: 40, frameMaterialMax: 40,
  frameMeasurementTenthsMax: 2_000,
  /** BR-CATALOGUE-008: lens details. */
  lensTypeMax: 20, lensDesignCodeMax: 20, lensMaterialCodeMax: 20, lensMaterialTypeMax: 40,
  lensIndexThousandthsMin: 1_400, lensIndexThousandthsMax: 2_000,
  progressionZoneLengthMmMin: 5, progressionZoneLengthMmMax: 30,
  lensDiameterMmMin: 40, lensDiameterMmMax: 90,
  sphereHundredthsMin: -3_000, sphereHundredthsMax: 3_000,
  cylinderHundredthsMin: -1_000, cylinderHundredthsMax: 1_000,
  addHundredthsMin: 0, addHundredthsMax: 500,
  /** BR-CATALOGUE-009 (W12): a contact lens's replacement interval and pack, both required; base curve and diameter more than 0 and at most 20.0 mm. */
  contactLensIntervalDaysMin: 1, contactLensIntervalDaysMax: 365,
  contactLensPackSizeMin: 1, contactLensPackSizeMax: 999,
  contactLensCurveTenthsMax: 200,
  /** BR-CATALOGUE-010: a service's duration hint, in whole minutes. */
  serviceDurationMinutesMin: 5, serviceDurationMinutesMax: 480,
  /** BR-CATALOGUE-013: an alternate barcode is 4–48 printable characters without spaces. */
  alternateBarcodeMin: 4, alternateBarcodeMax: 48,
  /** BR-CATALOGUE-016 (W4): a composite's 1–20 component lines, each 1–99 whole units. */
  compositeLinesMin: 1, compositeLinesMax: 20, compositeQuantityMin: 1, compositeQuantityMax: 99,
  /** BR-CATALOGUE-017: a lens component option's code and description. */
  optionCodeMax: 20, optionDescriptionMax: 80,
  /** BR-CATALOGUE-018: a service-combination rule. */
  ruleNameMax: 80, ruleMessageMax: 200, ruleMembersMin: 1, ruleMembersMax: 20,
  maxQuantityPerOrderMin: 1, maxQuantityPerOrderMax: 99, oncePerPatientDaysMin: 1, oncePerPatientDaysMax: 3_650,
} as const;

/**
 * BR-CATALOGUE-009 (W12): a contact lens is named by its frequency — the interval's modality word: 1 daily, 14 two-weekly,
 * 30 monthly, 90 quarterly, 180 six-monthly, 365 yearly, any other interval "every N days".
 */
export function contactLensModality(intervalDays: number): string {
  switch (intervalDays) {
    case 1: return "daily";
    case 14: return "two-weekly";
    case 30: return "monthly";
    case 90: return "quarterly";
    case 180: return "six-monthly";
    case 365: return "yearly";
    default: return `every ${intervalDays} days`;
  }
}

/** BR-CATALOGUE-009: the name shown with the modality and the pack — `Acuvue Oasys 1-Day · daily · 30 pack`. */
export function contactLensDisplayName(name: string, intervalDays: number, packSize: number): string {
  return `${name} · ${contactLensModality(intervalDays)} · ${packSize} pack`;
}

/** BR-CATALOGUE-009: the Codes column's frequency — `freq 1 day`, `freq 30 days`. */
export function contactLensCodesLabel(intervalDays: number): string {
  return `freq ${intervalDays} ${intervalDays === 1 ? "day" : "days"}`;
}

// ===========================================================================================================================
// The catalogue module's request and response schemas (UC-CATALOGUE-001..011; modules-and-api.md §5.10)
// ===========================================================================================================================

/** BR-CATALOGUE-013: the symbology a barcode prints in follows from its kind (labels: Sprint 13). */
export const BARCODE_SYMBOLOGIES = ["EAN-13", "UPC-A", "Code 128"] as const;
export type BarcodeSymbology = (typeof BARCODE_SYMBOLOGIES)[number];
export const symbologyOf = (kind: BarcodeKind): BarcodeSymbology => (kind === "SupplierUPC" ? "UPC-A" : kind === "Alternate" ? "Code 128" : "EAN-13");

/** BR-CATALOGUE-024: the tabs of Products, in their order; the two lens categories share the Lenses tab. */
export const PRODUCT_TABS = ["frames", "lenses", "contactLenses", "accessories", "services"] as const;
export type ProductTab = (typeof PRODUCT_TABS)[number];
export const TAB_OF_CATEGORY: Readonly<Record<ProductCategory, ProductTab>> = {
  Frame: "frames", PlasticLens: "lenses", GlassLens: "lenses", ContactLens: "contactLenses", Accessory: "accessories", Service: "services",
};

/** BR-CATALOGUE-024: a query of 2 to 100 characters; 50 rows a page by default, at most 200. */
export const PRODUCT_QUERY = { qMin: 2, qMax: 100, limitDefault: 50, limitMax: 200 } as const;
/** BR-CATALOGUE-025: a scanned or typed code, 1 to 64 characters. */
export const LOOKUP_CODE_MAX = 64;
/** BR-CATALOGUE-022: a deletion's reason, 1 to 500 characters. */
export const PRODUCT_VOID_REASON_MAX = 500;
/** UC-PRICING-001 A6: a lowered or cleared price's reason, for its Control Log entry — at most 500 characters. */
export const PRODUCT_PRICE_REASON_MAX = 500;

/** The warnings a catalogue write may answer beside its record (UC-CATALOGUE-001 A7, UC-CATALOGUE-005 A3). */
export const CATALOGUE_WARNING_CODES = ["TariffCodeNotInReference", "ComponentOfActiveComposite"] as const;
export type CatalogueWarningCode = (typeof CATALOGUE_WARNING_CODES)[number];

// --- Field builders: each answers the use cases' codes (`required`, `too_long`, `invalid`, `out_of_range`, `not_whole`) ------

type Ctx = z.RefinementCtx;
const fail = (ctx: Ctx, code: string, message: string): void => { ctx.addIssue({ code: "custom", message, params: { code } }); };
const absent = (v: unknown): boolean => v === undefined || v === null;
const blank = (v: unknown): boolean => absent(v) || (typeof v === "string" && v.trim() === "");

interface TextRule {
  max: number;
  /** Below `min` characters answers `shortCode` (a tariff code of one character is `invalid`, BR-CATALOGUE-012). */
  min?: number;
  shortCode?: string;
  pattern?: RegExp;
  patternMessage?: string;
}

function checkText(v: unknown, rule: TextRule, ctx: Ctx, required: boolean): void {
  if (blank(v)) { if (required) fail(ctx, "required", "required"); return; }
  if (typeof v !== "string") return fail(ctx, "invalid", "must be text");
  const s = v.trim();
  if (s.length > rule.max) return fail(ctx, "too_long", `at most ${rule.max} characters`);
  if (rule.min !== undefined && s.length < rule.min) return fail(ctx, rule.shortCode ?? "too_short", `at least ${rule.min} characters`);
  if (rule.pattern && !rule.pattern.test(s)) return fail(ctx, "invalid", rule.patternMessage ?? "not in the allowed format");
}

// Zod treats a key whose schema answers `undefined` as missing unless the schema is optional: an optional field is wrapped in
// `.optional()` (left out, it is undefined and its checks do not run); a required one is not, so a missing key reaches its
// own check and answers `required` rather than Zod's `invalid_type`.

/** Text that must be present: absent, null or blank is `required`; trimmed. */
const requiredText = (rule: TextRule) => z.unknown().superRefine((v, ctx) => checkText(v, rule, ctx, true)).transform((v) => (v as string).trim());
/** Text that an edit may leave out (unchanged) but never clear: `null` or blank is `required`; trimmed. */
const keptText = (rule: TextRule) => z.unknown().superRefine((v, ctx) => checkText(v, rule, ctx, true)).transform((v) => (v as string).trim()).optional();
/** Text a body may leave out (unchanged) or clear (`null` or blank); trimmed. */
const optionalText = (rule: TextRule) => z.unknown().superRefine((v, ctx) => checkText(v, rule, ctx, false))
  .transform((v): string | null => (blank(v) ? null : (v as string).trim())).optional();

const oneOfSchema = <T extends string>(values: readonly T[], required: boolean) => z.unknown()
  .superRefine((v, ctx) => {
    if (absent(v)) { if (required) fail(ctx, "required", "required"); return; }
    if (typeof v !== "string" || !(values as readonly string[]).includes(v)) fail(ctx, "invalid", `one of ${values.join(", ")}`);
  });
/** One of `values`, required. */
const requiredOneOf = <T extends string>(values: readonly T[]) => oneOfSchema(values, true).transform((v) => v as T);
/** One of `values` when given; `null` clears it. */
const oneOf = <T extends string>(values: readonly T[]) => oneOfSchema(values, false).transform((v) => v as T | null).optional();

/** A true or false, when given. */
const flagField = () => z.unknown().superRefine((v, ctx) => { if (typeof v !== "boolean") fail(ctx, "invalid", "true or false"); })
  .transform((v) => v as boolean).optional();

const idSchema = (required: boolean) => z.unknown().superRefine((v, ctx) => {
  if (absent(v)) { if (required) fail(ctx, "required", "required"); return; }
  if (typeof v !== "string" || v.trim().length === 0 || v.length > 64) fail(ctx, "invalid", "an id");
});
/** An id another record of the organisation must hold (whether it does is the service's 404), required. */
const requiredId = () => idSchema(true).transform((v) => (v as string).trim());
/** An id when given; `null` clears it. */
const idField = () => idSchema(false).transform((v) => (v === null ? null : (v as string).trim())).optional();

/** Whether `n` has at most `places` decimals (with a tolerance for binary fractions: 199.9 × 10 is 1998.999…). */
export function hasAtMostDecimals(n: number, places: number): boolean {
  const scaled = n * 10 ** places;
  return Math.abs(scaled - Math.round(scaled)) < 1e-6;
}

interface NumberRule {
  /** Decimals allowed: 0 is a whole number (a fraction is `not_whole`), more is a measurement (too many decimals is `invalid`). */
  places: number;
  min: number;
  max: number;
  /** `min` itself is out of range ("more than 0"). */
  minExclusive?: boolean;
  required?: boolean;
  unit?: string;
}

const numberSchema = (rule: NumberRule) => z.unknown()
  .superRefine((v, ctx) => {
    if (absent(v)) { if (rule.required) fail(ctx, "required", "required"); return; }
    if (typeof v !== "number" || !Number.isFinite(v)) return fail(ctx, "invalid", "a number");
    if (!hasAtMostDecimals(v, rule.places)) return fail(ctx, rule.places === 0 ? "not_whole" : "invalid", rule.places === 0 ? "a whole number" : `at most ${rule.places} decimal${rule.places === 1 ? "" : "s"}`);
    const low = rule.minExclusive ? v <= rule.min : v < rule.min;
    if (low || v > rule.max) fail(ctx, "out_of_range", `${rule.minExclusive ? "more than" : "from"} ${rule.min} to ${rule.max}${rule.unit ? ` ${rule.unit}` : ""}`);
  });
/** A number held to its decimals and range: required, or optional (`null` clears it). */
const numberField = (rule: NumberRule) => numberSchema(rule).transform((v) => v as number | null).optional();
const requiredNumber = (rule: Omit<NumberRule, "required">) => numberSchema({ ...rule, required: true }).transform((v) => v as number);

const centsSchema = (required: boolean, max?: number) => z.unknown().superRefine((v, ctx) => {
  if (absent(v)) { if (required) fail(ctx, "required", "required"); return; }
  if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) return fail(ctx, "invalid", "whole cents of at least 0");
  if (max !== undefined && v > max) fail(ctx, "out_of_range", `at most ${max} cents`);
});
/** Money in whole cents of at least 0 (BR-PRICING-001): anything else is `invalid`; `null` clears an optional amount. */
const centsField = () => centsSchema(false).transform((v) => v as number | null).optional();
const requiredCents = () => centsSchema(true).transform((v) => v as number);
/** A cost a person types, in whole cents from 0 to R 999 999.99 (BR-PRICING-001, UNIT_COST_MAX_CENTS): a larger one is `out_of_range`. */
const costField = () => centsSchema(false, L.supplierPriceCentsMax).transform((v) => v as number | null).optional();

const L = CATALOGUE_LIMITS;
/** BR-CATALOGUE-007: a frame's measurement, millimetres to one decimal, more than 0 and at most 200.0. */
const frameMm = () => numberField({ places: 1, min: 0, max: L.frameMeasurementTenthsMax / 10, minExclusive: true, unit: "mm" });
/** BR-CATALOGUE-008, -009: a power in dioptres to two decimals. */
const power = (min: number, max: number) => numberField({ places: 2, min: min / 100, max: max / 100, unit: "D" });

/** BR-CATALOGUE-003: the SKU — 1–40 letters, digits and - _ . / without spaces, trimmed. */
const SKU_RULE: TextRule = { max: 40, pattern: /^[A-Za-z0-9._/-]+$/, patternMessage: "letters, digits and - _ . / only, without spaces" };
/** BR-CATALOGUE-012: NAPPI (1–20) and tariff (2–12) codes, letters and digits, kept as text. */
const NAPPI_RULE: TextRule = { max: 20, pattern: /^[A-Za-z0-9]+$/, patternMessage: "letters and digits only" };
const TARIFF_RULE: TextRule = { max: 12, min: 2, shortCode: "invalid", pattern: /^[A-Za-z0-9]+$/, patternMessage: "2 to 12 letters and digits" };

// --- Category details (BR-CATALOGUE-006..010). One shape for an addition and an edit: an edit sends only what changes, `null`
// clears a field; what must be present after the change (a contact lens's interval and pack) is checked on the merged details.

export const FrameDetailsInputSchema = z.strictObject({
  frameType: oneOf(FRAME_TYPES),
  frameTypeCode: optionalText({ max: L.hoyalogFrameTypeCodeMax }),
  modelName: optionalText({ max: L.modelNameMax }),
  modelNumber: optionalText({ max: L.modelNumberMax }),
  colourCode: optionalText({ max: L.colourCodeMax }),
  colourName: optionalText({ max: L.colourNameMax }),
  eyeSize: frameMm(), bridge: frameMm(), bSize: frameMm(), ed: frameMm(), templeLength: frameMm(),
  genderAgeGroup: oneOf(FRAME_GENDER_AGE_GROUPS),
  material: optionalText({ max: L.frameMaterialMax }),
});
export type FrameDetailsInput = z.infer<typeof FrameDetailsInputSchema>;

export const LensDetailsInputSchema = z.strictObject({
  designType: oneOf(LENS_DESIGN_TYPES),
  lensType: optionalText({ max: L.lensTypeMax }),
  designCode: optionalText({ max: L.lensDesignCodeMax }),
  materialCode: optionalText({ max: L.lensMaterialCodeMax }),
  materialType: optionalText({ max: L.lensMaterialTypeMax }),
  refractiveIndex: numberField({ places: 3, min: L.lensIndexThousandthsMin / 1000, max: L.lensIndexThousandthsMax / 1000 }),
  progressionZoneLength: numberField({ places: 0, min: L.progressionZoneLengthMmMin, max: L.progressionZoneLengthMmMax, unit: "mm" }),
  diameter: numberField({ places: 0, min: L.lensDiameterMmMin, max: L.lensDiameterMmMax, unit: "mm" }),
  colour: oneOf(LENS_COLOURS),
  sphereMin: power(L.sphereHundredthsMin, L.sphereHundredthsMax), sphereMax: power(L.sphereHundredthsMin, L.sphereHundredthsMax),
  cylinderMin: power(L.cylinderHundredthsMin, L.cylinderHundredthsMax), cylinderMax: power(L.cylinderHundredthsMin, L.cylinderHundredthsMax),
  addMin: power(L.addHundredthsMin, L.addHundredthsMax), addMax: power(L.addHundredthsMin, L.addHundredthsMax),
  /** An active coating (and a tint) of the lens's own supplier, not restricted to another lens (BR-CATALOGUE-008, -017). */
  defaultCoatingOptionId: idField(),
  defaultColourOptionId: idField(),
});
export type LensDetailsInput = z.infer<typeof LensDetailsInputSchema>;

export const ContactLensDetailsInputSchema = z.strictObject({
  /** Whole days 1–365 (W12); required on a contact lens. */
  replacementIntervalDays: numberField({ places: 0, min: L.contactLensIntervalDaysMin, max: L.contactLensIntervalDaysMax, unit: "days" }),
  /** Lenses in a pack, 1–999 (W12); required on a contact lens. */
  lensesPerPack: numberField({ places: 0, min: L.contactLensPackSizeMin, max: L.contactLensPackSizeMax }),
  baseCurve: numberField({ places: 1, min: 0, max: L.contactLensCurveTenthsMax / 10, minExclusive: true, unit: "mm" }),
  diameter: numberField({ places: 1, min: 0, max: L.contactLensCurveTenthsMax / 10, minExclusive: true, unit: "mm" }),
  sphereMin: power(L.sphereHundredthsMin, L.sphereHundredthsMax), sphereMax: power(L.sphereHundredthsMin, L.sphereHundredthsMax),
  cylinderMin: power(L.cylinderHundredthsMin, L.cylinderHundredthsMax), cylinderMax: power(L.cylinderHundredthsMin, L.cylinderHundredthsMax),
  addMin: power(L.addHundredthsMin, L.addHundredthsMax), addMax: power(L.addHundredthsMin, L.addHundredthsMax),
});
export type ContactLensDetailsInput = z.infer<typeof ContactLensDetailsInputSchema>;

export const ServiceDetailsInputSchema = z.strictObject({
  isConsultation: flagField(),
  isFittingFee: flagField(),
  durationMinutes: numberField({ places: 0, min: L.serviceDurationMinutesMin, max: L.serviceDurationMinutesMax, unit: "minutes" }),
});
export type ServiceDetailsInput = z.infer<typeof ServiceDetailsInputSchema>;

/** BR-CATALOGUE-016 (W4): one component line of a composite — a product and a whole quantity 1–99. */
export const CompositeComponentInputSchema = z.strictObject({
  productId: requiredId(),
  quantity: requiredNumber({ places: 0, min: L.compositeQuantityMin, max: L.compositeQuantityMax }),
});
export type CompositeComponentInput = z.infer<typeof CompositeComponentInputSchema>;

/**
 * BR-CATALOGUE-013: a barcode's value for its kind. A supplier EAN is 13 digits with its GS1 check digit, never beginning `20`;
 * a UPC 12 digits with the UPC-A check digit; an alternate 4–48 printable characters without spaces, never a 13-digit code
 * beginning `20` and never shaped like a serial number; a generated barcode takes no value (the tenant sequencer gives it).
 */
export function barcodeValueError(kind: BarcodeKind, value: string): { code: string; message: string } | null {
  const gs1 = (digits: number, label: string): { code: string; message: string } | null => {
    if (!/^\d+$/.test(value)) return { code: "invalid", message: `a ${label} is digits only` };
    if (value.length !== digits) return { code: "invalid_length", message: `a ${label} has ${digits} digits` };
    if (digits === 13 && value.startsWith("20")) return { code: "reserved_prefix", message: "a 13-digit code beginning 20 is only ever the practice's own generated barcode" };
    let sum = 0;
    for (let i = 0; i < digits - 1; i++) sum += Number(value[digits - 2 - i]) * (i % 2 === 0 ? 3 : 1);
    if ((10 - (sum % 10)) % 10 !== Number(value[digits - 1])) return { code: "check_digit", message: "the check digit is wrong" };
    return null;
  };
  switch (kind) {
    case "SupplierEAN": return gs1(13, "supplier EAN");
    case "SupplierUPC": return gs1(12, "supplier UPC");
    case "Alternate": {
      if (value.length < L.alternateBarcodeMin) return { code: "too_short", message: `at least ${L.alternateBarcodeMin} characters` };
      if (value.length > L.alternateBarcodeMax) return { code: "too_long", message: `at most ${L.alternateBarcodeMax} characters` };
      if (!/^[\x21-\x7E]+$/.test(value)) return { code: "invalid", message: "printable characters without spaces" };
      if (/^\d{13}$/.test(value) && value.startsWith("20")) return { code: "reserved_prefix", message: "a 13-digit code beginning 20 is only ever the practice's own generated barcode" };
      if (/^\d{4}-\d{2}-\d{2}-\d{3,}$/.test(value)) return { code: "serial_shape", message: "a barcode never has the shape of a serial number" };
      return null;
    }
    case "Generated": return null;
  }
}

/** A barcode to add: its kind and value, or `Generated` and no value (UC-CATALOGUE-007 A2). */
export const BarcodeInputSchema = z.strictObject({
  kind: requiredOneOf(BARCODE_KINDS),
  value: z.unknown().optional(),
}).superRefine((v, ctx) => {
  const kind = v.kind as BarcodeKind | null | undefined;
  if (!kind || !(BARCODE_KINDS as readonly string[]).includes(kind)) return;
  const issue = (code: string, message: string) => ctx.addIssue({ code: "custom", path: ["value"], message, params: { code } });
  if (kind === "Generated") { if (!absent(v.value)) issue("not_allowed", "a generated barcode takes its number from the practice's counter"); return; }
  if (blank(v.value)) return issue("required", "required");
  if (typeof v.value !== "string") return issue("invalid", "text");
  const e = barcodeValueError(kind, v.value.trim());
  if (e) issue(e.code, e.message);
}).transform((v) => ({ kind: v.kind as BarcodeKind, value: typeof v.value === "string" ? v.value.trim() : null }));
export type BarcodeInput = z.infer<typeof BarcodeInputSchema>;

const productFields = {
  description: optionalText({ max: L.descriptionMax }),
  brand: optionalText({ max: L.brandMax }),
  subCategory: optionalText({ max: L.subCategoryMax }),
  tag: optionalText({ max: L.tagMax }),
  vatTreatment: oneOf(VAT_TREATMENTS),
  supplierId: idField(),
  supplierCode: optionalText({ max: L.supplierCodeMax }),
  nappiCode: optionalText(NAPPI_RULE),
  tariffCode: optionalText(TARIFF_RULE),
  stocked: flagField(),
  trackSerials: flagField(),
  isComposite: flagField(),
  frame: FrameDetailsInputSchema.nullable().optional(),
  lens: LensDetailsInputSchema.nullable().optional(),
  contactLens: ContactLensDetailsInputSchema.nullable().optional(),
  service: ServiceDetailsInputSchema.nullable().optional(),
  components: z.array(CompositeComponentInputSchema).nullable().optional(),
  /**
   * The tenant price, VAT-inclusive cents (W1, BR-PRICING-008), `null` for none — checked and saved through pricing in the same
   * unit of work as the product (UC-PRICING-001), which refuses anything but whole cents of at least 0 (`priceCents`).
   */
  priceCents: z.unknown().optional(),
  /**
   * The supplier price (CAT-29; BR-CATALOGUE-026): what the supplier charges for one, before any discount, ex-VAT whole cents from 0
   * to R 999 999.99, the organisation's alone; `null` clears it and a body that leaves it out leaves it as it is. Anything else is
   * `422 VALIDATION_FAILED` (`supplierPriceCents`, `invalid`; `out_of_range` above the cap). No Control PIN and no reason: it is a cost,
   * never a selling price (DVN's answers of 28 Oct 2026, change 9).
   */
  supplierPriceCents: costField(),
};

/** UC-CATALOGUE-001: a new product with its category's details, components and the barcodes scanned on the form. */
export const CreateProductInputSchema = z.strictObject({
  category: requiredOneOf(PRODUCT_CATEGORIES),
  sku: requiredText(SKU_RULE),
  name: requiredText({ max: L.nameMax }),
  ...productFields,
  barcodes: z.array(BarcodeInputSchema).optional(),
});
export type CreateProductInput = z.infer<typeof CreateProductInputSchema>;

/** UC-CATALOGUE-002: an edit under `If-Match` — only what changes; a detail object is merged with the stored details. */
export const PatchProductInputSchema = z.strictObject({
  category: oneOf(PRODUCT_CATEGORIES),
  sku: keptText(SKU_RULE),
  name: keptText({ max: L.nameMax }),
  ...productFields,
  /**
   * Why the price is lowered or cleared, written to the Control Log entry of the Control PIN that needs (UC-PRICING-001 A6;
   * BR-PRICING-017; DVN's answer of 24 Oct 2026 to Sprint 4's questions 1 and 14) and to the price's history entry; at most 500
   * characters. Required whenever the edit needs that PIN — refused `422` (`priceReason`, `required`) before it is asked (E8; DVN's
   * answer of 28 Oct 2026 to question 52 (b), change 11). Not kept on the product; ignored without a price change.
   */
  priceReason: optionalText({ max: PRODUCT_PRICE_REASON_MAX }),
});
export type PatchProductInput = z.infer<typeof PatchProductInputSchema>;

/** UC-CATALOGUE-006: a deletion's reason, 1–500 characters. */
export const ProductVoidInputSchema = z.strictObject({ reason: requiredText({ max: PRODUCT_VOID_REASON_MAX }) });
export type ProductVoidInput = z.infer<typeof ProductVoidInputSchema>;

// --- Lists, lookup and brands (UC-CATALOGUE-003, UC-CATALOGUE-004) --------------------------------------------------------

const limitParam = z.unknown().superRefine((v, ctx) => {
  const n = typeof v === "string" && /^\d+$/.test(v) ? Number(v) : NaN;
  if (!Number.isInteger(n) || n < 1 || n > PRODUCT_QUERY.limitMax) fail(ctx, "out_of_range", `1 to ${PRODUCT_QUERY.limitMax}`);
}).transform((v): number => Number(v)).default(PRODUCT_QUERY.limitDefault);
const queryFlag = oneOf(["true", "false"] as const);
const cursorParam = z.unknown().superRefine((v, ctx) => { if (typeof v !== "string" || v.length > 512) fail(ctx, "invalid", "a cursor this list issued"); })
  .transform((v) => v as string).optional();

/** `?category=Frame,PlasticLens` — one category or several, comma-separated. */
const categoriesParam = z.unknown().superRefine((v, ctx) => {
  const parts = typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : [];
  if (parts.length === 0 || parts.some((p) => !(PRODUCT_CATEGORIES as readonly string[]).includes(p))) fail(ctx, "invalid", `one or more of ${PRODUCT_CATEGORIES.join(", ")}, comma-separated`);
}).transform((v) => [...new Set((v as string).split(",").map((s) => s.trim()).filter(Boolean))] as ProductCategory[]).optional();

/** BR-CATALOGUE-024: `?q` of 2–100 characters (trimmed; blank is no search). */
const searchParam = z.unknown().superRefine((v, ctx) => {
  if (typeof v === "string" && v.trim() === "") return;
  if (typeof v !== "string") return fail(ctx, "invalid", "text");
  const s = v.trim();
  if (s.length < PRODUCT_QUERY.qMin) return fail(ctx, "too_short", `at least ${PRODUCT_QUERY.qMin} characters`);
  if (s.length > PRODUCT_QUERY.qMax) fail(ctx, "too_long", `at most ${PRODUCT_QUERY.qMax} characters`);
}).transform((v): string | undefined => ((v as string).trim() !== "" ? (v as string).trim() : undefined)).optional();

export const ProductListQuerySchema = z.object({
  q: searchParam,
  category: categoriesParam,
  supplierId: idField(),
  brand: optionalText({ max: L.brandMax }),
  includeInactive: queryFlag,
  notInLatestPriceList: queryFlag,
  limit: limitParam,
  cursor: cursorParam,
  count: queryFlag,
  /** The branch whose figures the rows carry — the acting branch unless named (BR-CATALOGUE-026). */
  branchId: idField(),
});
export type ProductListQuery = z.infer<typeof ProductListQuerySchema>;

export const ProductBrandsQuerySchema = z.object({ q: optionalText({ max: L.brandMax }) });
export const ProductBrandsResultSchema = z.object({ items: z.array(z.string()) });

/** BR-CATALOGUE-025: `?code` (1–64 characters), and `?category` for a picker of some categories. */
export const ProductLookupQuerySchema = z.object({
  code: z.unknown().superRefine((v, ctx) => {
    if (blank(v)) return fail(ctx, "required", "required");
    if (typeof v !== "string") return fail(ctx, "invalid", "text");
    if (v.trim().length > LOOKUP_CODE_MAX) fail(ctx, "too_long", `at most ${LOOKUP_CODE_MAX} characters`);
  }).transform((v) => (v as string).trim()),
  category: categoriesParam,
});
export type ProductLookupQuery = z.infer<typeof ProductLookupQuerySchema>;

// --- Views ------------------------------------------------------------------------------------------------------------------

export const CatalogueUserRefSchema = z.object({ id: z.string(), controlId: z.number().int().nullable(), displayName: z.string() });
export const CatalogueWarningSchema = z.object({ code: z.enum(CATALOGUE_WARNING_CODES), message: z.string(), detail: z.record(z.string(), z.unknown()).optional() });
export type CatalogueWarning = z.infer<typeof CatalogueWarningSchema>;
/** A supplier as a product or an option names it: kept when deactivated, and the panel says so (BR-CATALOGUE-011). */
export const CatalogueSupplierRefSchema = z.object({ id: z.string(), name: z.string(), active: z.boolean() });

export const FrameDetailsViewSchema = z.object({
  frameType: z.enum(FRAME_TYPES).nullable(), frameTypeCode: z.string().nullable(), modelName: z.string().nullable(), modelNumber: z.string().nullable(),
  colourCode: z.string().nullable(), colourName: z.string().nullable(),
  eyeSize: z.number().nullable(), bridge: z.number().nullable(), bSize: z.number().nullable(), ed: z.number().nullable(), templeLength: z.number().nullable(),
  genderAgeGroup: z.enum(FRAME_GENDER_AGE_GROUPS).nullable(), material: z.string().nullable(),
  /** The size marking, A-DBL-temple: `53-17-140` (BR-CATALOGUE-007). */
  sizeMarking: z.string().nullable(),
});
export type FrameDetailsView = z.infer<typeof FrameDetailsViewSchema>;
const rangeViews = {
  sphereMin: z.number().nullable(), sphereMax: z.number().nullable(), cylinderMin: z.number().nullable(), cylinderMax: z.number().nullable(),
  addMin: z.number().nullable(), addMax: z.number().nullable(),
};
export const LensDetailsViewSchema = z.object({
  designType: z.enum(LENS_DESIGN_TYPES).nullable(), lensType: z.string().nullable(), designCode: z.string().nullable(), materialCode: z.string().nullable(),
  materialType: z.string().nullable(), refractiveIndex: z.number().nullable(), progressionZoneLength: z.number().int().nullable(), diameter: z.number().int().nullable(),
  colour: z.enum(LENS_COLOURS).nullable(), ...rangeViews,
  defaultCoatingOptionId: z.string().nullable(), defaultColourOptionId: z.string().nullable(),
});
export type LensDetailsView = z.infer<typeof LensDetailsViewSchema>;
export const ContactLensDetailsViewSchema = z.object({
  replacementIntervalDays: z.number().int(), lensesPerPack: z.number().int(), modality: z.string(),
  baseCurve: z.number().nullable(), diameter: z.number().nullable(), ...rangeViews,
});
export type ContactLensDetailsView = z.infer<typeof ContactLensDetailsViewSchema>;
export const ServiceDetailsViewSchema = z.object({ isConsultation: z.boolean(), isFittingFee: z.boolean(), durationMinutes: z.number().int().nullable() });
export type ServiceDetailsView = z.infer<typeof ServiceDetailsViewSchema>;

export const CompositeComponentViewSchema = z.object({
  productId: z.string(), sku: z.string(), name: z.string(), quantity: z.number().int(), status: z.enum(PRODUCT_STATUSES),
  /** The component's tenant price (VAT-inclusive cents), the parts' total of the panel (BR-CATALOGUE-016). */
  priceCents: z.number().int().nullable(),
});
export type CompositeComponentView = z.infer<typeof CompositeComponentViewSchema>;

export const ProductBarcodeViewSchema = z.object({
  id: z.string(), productId: z.string(), kind: z.enum(BARCODE_KINDS), value: z.string(), symbology: z.enum(BARCODE_SYMBOLOGIES),
  createdAt: z.string(), createdBy: CatalogueUserRefSchema.nullable(),
});
export type ProductBarcodeView = z.infer<typeof ProductBarcodeViewSchema>;

/**
 * What a branch shows beside a product (BR-CATALOGUE-026): its cost, markup, price and stock, read from pricing and inventory
 * at the route (they register the readers; each figure is null until its module answers it).
 */
export const ProductBranchFiguresSchema = z.object({
  branchId: z.string(),
  costCents: z.number().int().nullable(),
  /**
   * Which leg gave `costCents`, said with it so a screen names it from this one read (UC-CATALOGUE-003 A14 step 2; the review of change
   * 9, 1 Nov 2026, C9R-CONTRACT-1): a made-to-order lens's lab cost from a supplier price list in force (`PriceList`), or — no list
   * carrying it — its supplier price net of its supplier's default discount (`SupplierPrice`; BR-PRICING-020). Null for a stock line's
   * average, which inventory gives, and wherever there is no cost.
   */
  costSource: z.enum(["PriceList", "SupplierPrice"]).nullable(),
  markupBp: z.number().int().nullable(),
  /**
   * Whose band, product markup or lens list gives `markupBp`: the organisation's (`Tenant`) or the branch's own (`Branch`, the
   * list's "Menlyn override"); null without a markup. Pricing's scopes (`PRICING_SCOPES`), spelled here because pricing's
   * contracts import the catalogue's.
   */
  markupScope: z.enum(["Tenant", "Branch"]).nullable(),
  priceCents: z.number().int().nullable(),
  priceSource: z.string().nullable(),
  stock: z.object({ onHand: z.number().int(), inStore: z.number().int(), reserved: z.number().int(), atLab: z.number().int(), damaged: z.number().int(), inTransit: z.number().int() }).nullable(),
  /**
   * The branch's last receipt of a stocked product — the day it was received, its unit cost ex VAT and the document it came on,
   * read from inventory's Received movements — on a product's own read only (UC-CATALOGUE-003 step 5: "last received 14 Aug 2026
   * at R 490.00"); the list's rows leave it out. Null when the branch has received none (TC-CATALOGUE-149).
   */
  lastReceipt: z.object({
    on: z.string(), unitCostCents: z.number().int().nullable(),
    document: z.object({ type: z.string(), id: z.string().nullable(), number: z.string().nullable() }).nullable(),
  }).nullable().optional(),
});
export type ProductBranchFigures = z.infer<typeof ProductBranchFiguresSchema>;

/** A product as lists, pickers and the lookup show it (UC-CATALOGUE-003, -004). */
export const ProductSummarySchema = z.object({
  id: z.string(), sku: z.string(), name: z.string(),
  /** The name as shown: a contact lens named by its frequency (BR-CATALOGUE-009). */
  displayName: z.string(),
  category: z.enum(PRODUCT_CATEGORIES), subCategory: z.string().nullable(), brand: z.string().nullable(), tag: z.string().nullable(),
  status: z.enum(PRODUCT_STATUSES), statusChangedAt: z.string().nullable(),
  isStockItem: z.boolean(), stocked: z.boolean(), trackSerials: z.boolean(), isComposite: z.boolean(),
  /** A stock item not kept in stock: "Made to order" where a stock figure would be (BR-CATALOGUE-002). */
  madeToOrder: z.boolean(),
  vatTreatment: z.enum(VAT_TREATMENTS),
  supplier: CatalogueSupplierRefSchema.nullable(), supplierCode: z.string().nullable(),
  nappiCode: z.string().nullable(), tariffCode: z.string().nullable(),
  /** The tenant price, VAT-inclusive cents, written through pricing; null is "No price" (UC-PRICING-001). */
  priceCents: z.number().int().nullable(), priceSource: z.string().nullable(),
  /**
   * The supplier price, ex-VAT cents before any discount; null for none (BR-CATALOGUE-026). Every role reads it wherever the product's
   * selling price is read — its own read, its list row, a picker's and the lookup's answer (W6; DVN's answers of 28 Oct 2026, change 9).
   */
  supplierPriceCents: z.number().int().nullable(),
  notInLatestPriceList: z.boolean(), notInPriceListSince: z.string().nullable(),
  /** The Codes column: `NAPPI 714512001`, a lens's supplier code, `freq 1 day`, `SAOA 1001` (UC-CATALOGUE-003 step 2). */
  codes: z.array(z.string()),
});
export type ProductSummary = z.infer<typeof ProductSummarySchema>;

export const ProductListItemSchema = ProductSummarySchema.extend({ atBranch: ProductBranchFiguresSchema.nullable() });
export type ProductListItem = z.infer<typeof ProductListItemSchema>;

/** Each tab's count under the same search and filters (BR-CATALOGUE-024), with `?count=true`. */
export const ProductTabCountsSchema = z.object({
  all: z.number().int(), frames: z.number().int(), lenses: z.number().int(), contactLenses: z.number().int(), accessories: z.number().int(), services: z.number().int(),
});
export type ProductTabCounts = z.infer<typeof ProductTabCountsSchema>;
export const ProductPageSchema = z.object({
  items: z.array(ProductListItemSchema),
  nextCursor: z.string().optional(),
  totalCount: z.number().int().optional(),
  counts: ProductTabCountsSchema.optional(),
});

export const ProductViewSchema = ProductSummarySchema.extend({
  description: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(), createdBy: CatalogueUserRefSchema.nullable(), updatedAt: z.string(),
  /** Whether any branch has held it — a stock movement or a serial anywhere; then Kept in stock and Serialised are locked (BR-CATALOGUE-004). */
  heldByBranch: z.boolean(),
  /**
   * A count in progress — open, or posting an opening stock in parts — that has a quantity line it froze or an entry of it, at any branch,
   * and its branch;
   * null when none. While one does, Kept in stock and Serialised are locked as well, so a count never posts stock its product
   * contradicts (BR-CATALOGUE-004, BR-INVENTORY-030; UC-CATALOGUE-002 E4; the closing check of 25 Oct 2026, FR-1 and CHKZ-fresh-1).
   * `frozenLine`: whether the count froze a quantity line of it when it started — a full or cycle count does for each stock line in its
   * scope — which holds it whatever its entries: removing them frees nothing, the count abandoned or finalised with none of it counted
   * does (the verification of round 6's fixes, 25 Oct 2026, L6I-3).
   */
  countInProgress: z.object({ stockTakeId: z.string(), number: z.string(), branchId: z.string(), branchName: z.string(), status: z.enum(["Open", "Posting"]), frozenLine: z.boolean() }).nullable(),
  /** Whether the tariff code is in the platform's tariff reference in effect today; null without a tariff code (BR-CATALOGUE-012). */
  tariffCodeInReference: z.boolean().nullable(),
  frame: FrameDetailsViewSchema.nullable(), lens: LensDetailsViewSchema.nullable(), contactLens: ContactLensDetailsViewSchema.nullable(), service: ServiceDetailsViewSchema.nullable(),
  components: z.array(CompositeComponentViewSchema),
  barcodes: z.array(ProductBarcodeViewSchema),
  /**
   * The import and the row of its file that created it (UC-CATALOGUE-003 A9, BR-IMPORTS-017), and whether that import has made its
   * list current — `applied` false while its Apply runs or stopped, or once it is discarded, when the lens is not activated by
   * hand (UC-CATALOGUE-005 E3), so a screen offers Reactivate only where the server allows it (TC-CATALOGUE-148).
   */
  importedFrom: z.object({ importJobId: z.string(), rowNo: z.number().int().nullable(), applied: z.boolean() }).nullable(),
  /**
   * The supplier's list whose final write hid it from new orders — its code on no current list any longer (BR-CATALOGUE-020;
   * DVN's answer of 24 Oct 2026, the board's question 4) — and the import that applied it; null for any other product. Such a
   * product is reactivated by no person, only by a later list carrying its code (UC-CATALOGUE-005 A5, E4; TC-CATALOGUE-164).
   */
  hiddenByPriceList: z.object({ priceListId: z.string(), name: z.string(), listDate: z.string(), importJobId: z.string() }).nullable(),
  atBranch: ProductBranchFiguresSchema.nullable(),
});
export type ProductView = z.infer<typeof ProductViewSchema>;
export const ProductWriteResultSchema = ProductViewSchema.extend({ warnings: z.array(CatalogueWarningSchema) });
export type ProductWriteResult = z.infer<typeof ProductWriteResultSchema>;

export const ProductVoidResultSchema = z.object({
  id: z.string(), sku: z.string(), name: z.string(), voidedAt: z.string(), voidedBy: CatalogueUserRefSchema.nullable(), reason: z.string(),
  controlLogId: z.string().nullable(),
});
export type ProductVoidResult = z.infer<typeof ProductVoidResultSchema>;

/** BR-CATALOGUE-025: what a code resolved to — a serial (its product, the serial or only its branch), a barcode, an SKU, a tag. */
export const LOOKUP_MATCHES = ["serial", "barcode", "sku", "tag"] as const;
export const ProductLookupSerialSchema = z.object({
  branchId: z.string(), branchName: z.string(),
  /** A serial held at a branch the user may not read: its product and branch only (UC-CATALOGUE-004 A5). */
  detailsWithheld: z.boolean(),
  id: z.string().nullable(), serialNumber: z.string().nullable(), status: z.enum(SERIAL_STATUSES).nullable(),
});
export const ProductLookupResultSchema = z.object({
  code: z.string(),
  matchedOn: z.enum(LOOKUP_MATCHES),
  items: z.array(z.object({ product: ProductSummarySchema, serial: ProductLookupSerialSchema.nullable() })),
});
export type ProductLookupResult = z.infer<typeof ProductLookupResultSchema>;

// --- Lens component options (UC-CATALOGUE-009; BR-CATALOGUE-017) --------------------------------------------------------

const optionFields = {
  tariffCode: optionalText(TARIFF_RULE),
  /** Ex-VAT cents (W1), optional. */
  costCents: centsField(),
  /** Restricted to one live lens product of the same supplier, or to none. */
  appliesToProductId: idField(),
  /** A tint only: 0–100 %. */
  saturation: numberField({ places: 0, min: 0, max: 100, unit: "%" }),
  gradient: flagField(),
  sortOrder: numberField({ places: 0, min: 0, max: 9_999 }),
};
export const CreateLensComponentOptionInputSchema = z.strictObject({
  supplierId: requiredId(),
  kind: requiredOneOf(LENS_COMPONENT_OPTION_KINDS),
  supplierCode: requiredText({ max: L.optionCodeMax }),
  description: requiredText({ max: L.optionDescriptionMax }),
  /** VAT-inclusive cents (W1), required. */
  sellingPriceCents: requiredCents(),
  ...optionFields,
});
export type CreateLensComponentOptionInput = z.infer<typeof CreateLensComponentOptionInputSchema>;
export const PatchLensComponentOptionInputSchema = z.strictObject({
  supplierCode: keptText({ max: L.optionCodeMax }),
  description: keptText({ max: L.optionDescriptionMax }),
  /** Never cleared: an option always has its selling price. */
  sellingPriceCents: requiredCents().optional(),
  isActive: flagField(),
  ...optionFields,
});
export type PatchLensComponentOptionInput = z.infer<typeof PatchLensComponentOptionInputSchema>;
export const LensComponentOptionListQuerySchema = z.object({
  supplierId: idField(),
  kind: oneOf(LENS_COMPONENT_OPTION_KINDS),
  /** The options a lens offers: its supplier's, not restricted to another lens. */
  productId: idField(),
  includeInactive: queryFlag,
  limit: limitParam,
  cursor: cursorParam,
});
export const LensComponentOptionViewSchema = z.object({
  id: z.string(), supplier: CatalogueSupplierRefSchema, kind: z.enum(LENS_COMPONENT_OPTION_KINDS), kindLabel: z.string(),
  supplierCode: z.string(), description: z.string(), tariffCode: z.string().nullable(),
  costCents: z.number().int().nullable(), sellingPriceCents: z.number().int(),
  appliesTo: z.object({ productId: z.string(), sku: z.string(), name: z.string() }).nullable(),
  saturation: z.number().int().nullable(), gradient: z.boolean(), sortOrder: z.number().int(), isActive: z.boolean(),
  importedFrom: z.object({ importJobId: z.string(), rowNo: z.number().int().nullable() }).nullable(),
  version: z.number().int(), createdAt: z.string(), updatedAt: z.string(),
});
export type LensComponentOptionView = z.infer<typeof LensComponentOptionViewSchema>;
/** An option as added or amended, with the save's warnings — a tariff code the reference does not hold (BR-CATALOGUE-012). */
export const LensComponentOptionWriteResultSchema = LensComponentOptionViewSchema.extend({ warnings: z.array(CatalogueWarningSchema) });
export type LensComponentOptionWriteResult = z.infer<typeof LensComponentOptionWriteResultSchema>;
export const LensComponentOptionPageSchema = z.object({ items: z.array(LensComponentOptionViewSchema), nextCursor: z.string().optional() });

// --- Service-combination rules (UC-CATALOGUE-010; BR-CATALOGUE-018) -----------------------------------------------------

export const ServiceCombinationMemberInputSchema = z.strictObject({
  productId: requiredId(),
  role: oneOf(SERVICE_COMBINATION_MEMBER_ROLES),
});
/** A whole number; its place and range depend on the rule type (BR-CATALOGUE-018), checked with the type. */
const ruleParameter = numberField({ places: 0, min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER });
export const CreateServiceCombinationRuleInputSchema = z.strictObject({
  name: requiredText({ max: L.ruleNameMax }),
  ruleType: requiredOneOf(SERVICE_COMBINATION_RULE_TYPES),
  parameter: ruleParameter,
  severity: requiredOneOf(SERVICE_COMBINATION_SEVERITIES),
  message: requiredText({ max: L.ruleMessageMax }),
  members: z.array(ServiceCombinationMemberInputSchema).optional(),
});
export type CreateServiceCombinationRuleInput = z.infer<typeof CreateServiceCombinationRuleInputSchema>;
export const PatchServiceCombinationRuleInputSchema = z.strictObject({
  name: keptText({ max: L.ruleNameMax }),
  ruleType: oneOf(SERVICE_COMBINATION_RULE_TYPES),
  parameter: ruleParameter,
  severity: oneOf(SERVICE_COMBINATION_SEVERITIES),
  message: keptText({ max: L.ruleMessageMax }),
  members: z.array(ServiceCombinationMemberInputSchema).optional(),
  isActive: flagField(),
});
export type PatchServiceCombinationRuleInput = z.infer<typeof PatchServiceCombinationRuleInputSchema>;
export const ServiceCombinationRuleListQuerySchema = z.object({ includeInactive: queryFlag, limit: limitParam, cursor: cursorParam });
export const ServiceCombinationRuleViewSchema = z.object({
  id: z.string(), name: z.string(), ruleType: z.enum(SERVICE_COMBINATION_RULE_TYPES), parameter: z.number().int().nullable(),
  severity: z.enum(SERVICE_COMBINATION_SEVERITIES), message: z.string(), isActive: z.boolean(),
  members: z.array(z.object({ productId: z.string(), sku: z.string(), name: z.string(), role: z.enum(SERVICE_COMBINATION_MEMBER_ROLES), status: z.enum(PRODUCT_STATUSES) })),
  version: z.number().int(), createdAt: z.string(), updatedAt: z.string(),
});
export type ServiceCombinationRuleView = z.infer<typeof ServiceCombinationRuleViewSchema>;
export const ServiceCombinationRulePageSchema = z.object({ items: z.array(ServiceCombinationRuleViewSchema), nextCursor: z.string().optional() });

// --- Catalogue settings (UC-CATALOGUE-011; BR-CATALOGUE-029, BR-PRICING-018; W7, R1) ----------------------------------

/** The namespaces `/catalogue-settings` reads and writes at the organisation's scope. */
export const CATALOGUE_SETTING_NAMESPACES = ["catalogue", "pricing", "stock"] as const;
export type CatalogueSettingNamespace = (typeof CATALOGUE_SETTING_NAMESPACES)[number];
const namespaceValues = z.record(z.string(), ResolvedSettingSchema);
export const CatalogueSettingsViewSchema = z.object({ catalogue: namespaceValues, pricing: namespaceValues, stock: namespaceValues });
export type CatalogueSettingsView = z.infer<typeof CatalogueSettingsViewSchema>;
/** Each namespace a map of its keys to values; a key left out is unchanged; the VAT rate is the organisation's, not here. */
export const CatalogueSettingsPatchSchema = z.strictObject({
  catalogue: z.record(z.string().min(1).max(64), z.unknown()).optional(),
  pricing: z.record(z.string().min(1).max(64), z.unknown()).optional(),
  stock: z.record(z.string().min(1).max(64), z.unknown()).optional(),
});
export type CatalogueSettingsPatch = z.infer<typeof CatalogueSettingsPatchSchema>;
